import classnames from 'classnames';
import I18n from 'i18n-js';
import React, { useContext } from 'react';

import { TagType } from '@reverbdotcom/commons/src/rc/types';
import { RCTag } from '@reverbdotcom/commons/src/rc';
import { RCToggledContent } from '@reverbdotcom/commons/src/rc/RCToggledContent';

import { SellFormContext } from '../SellFormContext';
import { Section } from '../sellFormViewSections';
import SellFormSectionButtons, { ButtonType } from './SellFormSectionButtons';
import SellFormOptOut from './SellFormOptOut';
import { Field } from '../sellFormReducer';


// Buttons that appear at the top right of the form
function getHeaderButtons(isDraft: boolean, onReviewPage: boolean): ButtonType[] {
  if (isDraft && onReviewPage) {
    return [ButtonType.PUBLISH];
  }

  if (!isDraft && !onReviewPage) {
    return [ButtonType.DISCARD, ButtonType.CONTINUE];
  }

  return [];
}

function getFooterButtons(isDraft: boolean, onReviewPage: boolean): ButtonType[] {
  if (isDraft && onReviewPage) {
    return [ButtonType.PREVIOUS_SECTION, ButtonType.PUBLISH];
  }

  if (isDraft && !onReviewPage) {
    return [ButtonType.PREVIOUS_SECTION, ButtonType.CONTINUE];
  }

  if (!isDraft && !onReviewPage) {
    return [ButtonType.DISCARD, ButtonType.CONTINUE];
  }

  return [];
}

function SellFormSectionCard({ children }) {
  return (
    <div className="sell-form-section__card">
      {children}
    </div>
  );
}

interface IToggledSectionProps {
  title: string;
  children: React.ReactNode;
  id: string;
  defaultOpen?: boolean;
  additionalHeadingContent?: React.ReactNode;
}

function ToggledSection({
  title,
  children,
  id,
  defaultOpen = false,
  additionalHeadingContent,
}: IToggledSectionProps) {
  return (
    <FormSection togglable>
      <RCToggledContent
        id={id}
        heading={title}
        defaultOpen={defaultOpen}
        additionalHeadingContent={additionalHeadingContent}
      >
        {children}
      </RCToggledContent>
    </FormSection>
  );
}
interface IFormSectionProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  tag?: TagType;
  includeBottomBorder?: boolean;
  fullWidthForm?: boolean;
  togglable?: boolean;
}

function FormSection({
  title,
  subtitle,
  children,
  tag,
  includeBottomBorder = false,
  fullWidthForm = false,
  togglable = false,
}: IFormSectionProps) {
  const classes = classnames(
    'sell-form-section__form-section',
    {
      'sell-form-section__form-section--bottom-border': includeBottomBorder,
      'sell-form-section__form-section--condensed-form': !fullWidthForm,
      'sell-form-section__form-section--toggled-section': togglable,
    },
  );

  return (
    <div className={classes}>
      {title && (
        <div className="sell-form-section__form-section__header">
          <span className="sell-form-section__form-section__header__title">
            {title}
          </span>
          {tag && tag !== 'none' && (
            <div className="sell-form-section__form-section__header__tag">
              <RCTag variant={tag === 'recommended' ? 'highlight' : null}>
                {I18n.t(`commons.formGroup.${tag}`)}
              </RCTag>
            </div>
          )}

          {subtitle && (
            <p className="sell-form-section__form-section__header__subtitle">
              {subtitle}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

interface IProps {
  children: React.ReactNode;
  additionalFooterContent?: React.ReactNode;
  disableSubmit?: boolean;
  scrollRef?: React.MutableRefObject<HTMLDivElement>;
}

export function SellFormSection({
  children,
  additionalFooterContent,
  disableSubmit = false,
  scrollRef,
}: IProps) {
  const {
    onSubmit,
    nextSection,
    loading,
    isDraft,
    formState,
  } = useContext(SellFormContext);

  const onReviewPage = !nextSection;
  const shouldJumpToReviewSection = !isDraft && !onReviewPage;
  const submitTargetSection = shouldJumpToReviewSection ? Section.REVIEW : nextSection;
  const headerButtons = getHeaderButtons(isDraft, onReviewPage);
  const showHeaderButtons = headerButtons.length > 0 || shouldJumpToReviewSection;
  const listingId = formState && formState[Field.ID];

  const footerClasses = classnames(
    'sell-form-section__footer',
    {
      'sell-form-section__footer--submit-disabled': loading || disableSubmit,
    },
  );

  return (
    <form
      className="sell-form-section"
      onSubmit={(e) => {
        e.preventDefault();
        if (!loading && !disableSubmit) {
          onSubmit(submitTargetSection);
        } else if (scrollRef?.current) {
          scrollRef.current.scrollIntoView();
        }
      }}
    >
      {showHeaderButtons && (
        <div className="sell-form-section__header__button__row">
          <div className="sell-form-section__header__buttons__main">
            <SellFormSectionButtons buttons={headerButtons} disableSubmit={disableSubmit}/>
          </div>

          {shouldJumpToReviewSection && (
            <div>
              <SellFormSectionButtons buttons={[ButtonType.GO_TO_REVIEW]} disableSubmit={disableSubmit}/>
            </div>
          )}
        </div>
      )}

      {children}

      <div className="sell-form-section__mobile-additional-content">
        {additionalFooterContent}
        <SellFormOptOut listingId={listingId} dropdownId="sell-form-opt-out-mobile" />
      </div>

      <div className={footerClasses}>
        <div className="sell-form-section__footer__additional-content">
          <SellFormOptOut listingId={listingId} dropdownId="sell-form-opt-out-desktop" />
          {additionalFooterContent}
        </div>

        <div className="sell-form-section__footer__actions">
          <SellFormSectionButtons buttons={getFooterButtons(isDraft, onReviewPage)} disableSubmit={disableSubmit}/>
        </div>
      </div>
    </form>
  );
}

SellFormSection.FormSection = FormSection;
SellFormSection.Card = SellFormSectionCard;
SellFormSection.ToggledSection = ToggledSection;
